// src/utils/gradients.js
// Utilería para construir degradados limpios a partir de los colores
// personalizados del nutriólogo (color1, color2, color3).
//
// Reglas de diseño:
// - Los degradados usan SOLO DOS colores (color1 -> color2). Mezclar los
//   tres en una misma franja se ve sucio.
// - La interpolación se hace en HSL siguiendo el arco de matiz más corto,
//   para que la transición pase por colores saturados y no por tonos
//   lodosos (problema clásico de interpolar en RGB, ej. verde -> naranja).
// - color3 se usa como color de acento en el resto del diseño.

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const DEFAULT_BRAND_COLORS = ["#8e2de2", "#4a00e0", "#7128ff"];

export const normalizeHex = (value, fallback = null) => {
  if (typeof value !== "string") return fallback;
  let hex = value.trim();
  if (!hex) return fallback;
  if (!hex.startsWith("#")) hex = `#${hex}`;
  if (!HEX_RE.test(hex)) return fallback;
  if (hex.length === 4) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex.toLowerCase();
};

export const hexToRgb = (hex) => {
  const value = normalizeHex(hex) || "#000000";
  return {
    r: parseInt(value.slice(1, 3), 16),
    g: parseInt(value.slice(3, 5), 16),
    b: parseInt(value.slice(5, 7), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const channel = (c) =>
    Math.max(0, Math.min(255, Math.round(c)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
};

export const withAlpha = (hex, alpha = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb = [0, 0, 0];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = l - c / 2;
  return {
    r: (rgb[0] + m) * 255,
    g: (rgb[1] + m) * 255,
    b: (rgb[2] + m) * 255,
  };
};

const hexToHsl = (hex) => rgbToHsl(hexToRgb(hex));
const hslToHex = (hsl) => rgbToHex(hslToRgb(hsl));

// Mezcla dos colores HSL siguiendo el arco de matiz más corto.
const mixHsl = (a, b, t) => {
  let dh = b.h - a.h;
  if (Math.abs(dh) > 180) dh -= Math.sign(dh) * 360;
  return {
    h: (a.h + dh * t + 360) % 360,
    s: a.s + (b.s - a.s) * t,
    l: a.l + (b.l - a.l) * t,
  };
};

export const relativeLuminance = (hex) => {
  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

// Color de texto legible (blanco u oscuro) sobre un conjunto de colores.
export const readableTextOn = (colors) => {
  const list = (colors || []).map((c) => normalizeHex(c)).filter(Boolean);
  if (!list.length) return "#ffffff";
  const avg =
    list.reduce((acc, color) => acc + relativeLuminance(color), 0) /
    list.length;
  return avg > 0.55 ? "#0f172a" : "#ffffff";
};

// Versión del color segura como acento sobre fondos claros: si es muy
// claro (amarillo pastel, etc.) se oscurece hasta que sea legible.
export const readableAccent = (hex, { maxLuminance = 0.55 } = {}) => {
  const value = normalizeHex(hex);
  if (!value) return "#475569";
  let hsl = hexToHsl(value);
  let result = value;
  let guard = 0;
  while (relativeLuminance(result) > maxLuminance && guard < 12) {
    hsl = { ...hsl, l: Math.max(0.28, hsl.l - 0.06) };
    result = hslToHex(hsl);
    guard += 1;
  }
  return result;
};

// Colores de marca del nutriólogo, validados y sin duplicados consecutivos.
export const getBrandColors = (source = {}) => {
  const cleaned = [source?.color1, source?.color2, source?.color3].map(
    (color, index) => normalizeHex(color, DEFAULT_BRAND_COLORS[index])
  );
  const deduped = cleaned.filter(
    (color, index) => index === 0 || color !== cleaned[index - 1]
  );
  return deduped.length ? deduped : [...DEFAULT_BRAND_COLORS];
};

// Degradado lineal suave con paradas intermedias interpoladas en HSL.
export const buildSmoothGradient = (
  colors,
  { angle = 135, stopsPerSegment = 5 } = {}
) => {
  const list = (colors || []).map((c) => normalizeHex(c)).filter(Boolean);
  if (!list.length) list.push(...DEFAULT_BRAND_COLORS.slice(0, 2));

  if (list.length === 1) {
    const base = hexToHsl(list[0]);
    const light = { ...base, l: Math.min(0.92, base.l + 0.14) };
    const dark = { ...base, l: Math.max(0.08, base.l - 0.16) };
    return `linear-gradient(${angle}deg, ${hslToHex(light)} 0%, ${list[0]} 50%, ${hslToHex(dark)} 100%)`;
  }

  const hslColors = list.map(hexToHsl);
  const segments = hslColors.length - 1;
  const stops = [];
  hslColors.forEach((color, index) => {
    stops.push(`${hslToHex(color)} ${((index / segments) * 100).toFixed(1)}%`);
    if (index < segments) {
      for (let step = 1; step <= stopsPerSegment; step += 1) {
        const t = step / (stopsPerSegment + 1);
        const position = ((index + t) / segments) * 100;
        stops.push(
          `${hslToHex(mixHsl(color, hslColors[index + 1], t))} ${position.toFixed(1)}%`
        );
      }
    }
  });
  return `linear-gradient(${angle}deg, ${stops.join(", ")})`;
};

// Degradado de marca: SOLO los dos primeros colores distintos del
// nutriólogo. Nunca mezcla los tres.
export const buildBrandGradient = (colors, options = {}) => {
  const list = (colors || []).map((c) => normalizeHex(c)).filter(Boolean);
  const pair = [];
  for (const color of list) {
    if (!pair.includes(color)) pair.push(color);
    if (pair.length === 2) break;
  }
  const finalPair = pair.length ? pair : DEFAULT_BRAND_COLORS.slice(0, 2);
  return buildSmoothGradient(finalPair, options);
};
